import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  CncOrderDeliveryPaymentType,
  ConsultOrderPrefillResponse,
  ConsultPharmacySelection,
  PersonalIdType,
  UserAddressRequestPayload,
} from "../types/consultOrder.types";
import { ItemInMarketplaceBasicResponse } from "../types/marketplaceItemSearch.types";
import { CONSULT_LOYALTY_PAYMENT_ENABLED } from "../utils/consultOrder.constants";

// ITAM-2583: owns the caller PII (prefill) and the in-memory order DRAFT for the
// current consult. Kept out of Redux and out of browser storage (privacy posture).
// Every consult-order form component reads/writes the draft THROUGH this context,
// so the components stay decoupled from one another (the context is the shared bus).
//
// ITAM-2887: the advisor has no fixed pharmacy, so each draft (CnC order and
// RFZO request) carries its own picked `pharmacy`. The CnC order is a pickup
// or a delivery ("Pomoć pri preuzimanju"); the delivery pre-check results are
// keyed on the picked pair, so a pharmacy change invalidates them.

export type ConsultOrderFulfillment =
  | "PICKUP"
  | "STANDARD_DELIVERY"
  | "FAST_DELIVERY";

export type ConsultOrderAddressMode = "SAVED" | "NEW";

/**
 * Result of the last delivery zone pre-check, tagged with the selection KEY
 * (pharmacy pair + delivery type + resolved coordinates) it was computed for.
 * Validation compares this key against the CURRENT selection so a stale
 * approval (pharmacy, address or delivery type changed since the check) never
 * counts as verified: it blocks until a fresh check for the new selection
 * completes.
 *   inZone: true  -> verified in zone     (allow)
 *   inZone: false -> verified out of zone (block)
 *   inZone: null  -> could not verify (403 / missing pharmacy coordinates) -> degrade
 */
export interface DeliveryZoneCheck {
  key: string;
  inZone: boolean | null;
}

/**
 * Resolved branch delivery fee for the current fulfillment, mirrored from
 * DeliverySection (which fetches the branch SERVICE items) and tagged with the
 * pharmacy pair and delivery type it was resolved for. Validation recomputes
 * the key from the CURRENT draft so a fee resolved for another pharmacy or
 * type never counts, and the lifecycle submit() reads `serviceItemId`/`price`
 * to build the payload.
 *   status "ok"        -> price is a usable number (incl. a genuine 0) -> allow
 *   status "loading"   -> the SERVICE items are still being fetched   -> block
 *   "missing"/"duplicate"/"malformed"/"error" -> no usable fee        -> block
 */
export type DeliveryServiceFeeStatus =
  | "loading"
  | "ok"
  | "missing"
  | "duplicate"
  | "malformed"
  | "error";

export interface DeliveryServiceFee {
  key: string; // deliveryFeeKey(pair, fulfillment) it was resolved for
  status: DeliveryServiceFeeStatus;
  serviceItemId: number | null;
  price: number | null; // usable number (incl. 0) only when status === "ok"
}

export interface ConsultOrderLine {
  item: ItemInMarketplaceBasicResponse;
  quantity: number;
}

export interface ConsultOrderDraft {
  /** The pharmacy the advisor picked for this reservation; null until picked. */
  pharmacy: ConsultPharmacySelection | null;
  items: ConsultOrderLine[];
  fulfillment: ConsultOrderFulfillment;

  // Delivery-only (ignored and excluded from payload/validation for PICKUP)
  addressMode: ConsultOrderAddressMode;
  selectedAddressId: number | null; // saved-address id
  newAddress: UserAddressRequestPayload | null; // Places-resolved (slice 2)
  saveNewAddress: boolean; // always an explicit boolean; default false
  paymentType: CncOrderDeliveryPaymentType;
  personalIdType: PersonalIdType;
  personalId: string;
  deliveryTimeSlot: { start: string; end: string } | null; // STANDARD only
  noteForDelivery: string;
  /**
   * The caller's spoken consent to forward their personal data and address
   * for the delivery. UI-only; the backend has no field for it and it is
   * never sent.
   */
  deliveryConsent: boolean;
  // Latest delivery zone pre-check, mirrored from DeliverySection. See
  // DeliveryZoneCheck.
  deliveryZoneCheck?: DeliveryZoneCheck | null;
  // Latest resolved branch delivery (SERVICE) fee, mirrored from
  // DeliverySection. See DeliveryServiceFee.
  deliveryServiceFee?: DeliveryServiceFee | null;

  // Customer (prefilled, editable)
  firstName: string;
  secondName: string;
  phoneNumber: string;
  note: string;
}

// ITAM-2887: the consult RFZO request draft (an EMPTY request: the pharmacy
// adds the medicines later). A SEPARATE field on the same context (never
// merged into the CnC order draft) so the advisor can switch between the two
// modes through the prompt while both stay intact. Same privacy posture:
// memory only, never Redux / browser storage.
export interface ConsultRfzoDraft {
  /** The pharmacy the caller chose for this request; null until picked. */
  pharmacy: ConsultPharmacySelection | null;
  lbo: string;
  // The advisor may identify the patient by LBO, by JMBG, or by both; the
  // backend requires at least one. Never prefilled: the consult API carries
  // no therapy JMBG.
  jmbg: string;
  // The patient snapshot on the request (prefilled from the consult).
  firstName: string;
  secondName: string;
  userPhone: string;
  /** Optional note for the pharmacy (sent as `note`). */
  note: string;
  /**
   * ITAM-2887 (Q10): the caller's spoken consent to forward their JMBG to the
   * pharmacy. UI-only; the backend has no field for it and it is never sent.
   */
  consent: boolean;
}

export interface ConsultOrderContextValue {
  prefill: ConsultOrderPrefillResponse | null;
  draft: ConsultOrderDraft;
  rfzoDraft: ConsultRfzoDraft;
  hydrate: (prefill: ConsultOrderPrefillResponse) => void;
  updateDraft: (patch: Partial<ConsultOrderDraft>) => void;
  /**
   * Picks (or changes) the reservation pharmacy. A CHANGE clears the items and
   * the delivery pre-check results (zone, fee, time slot).
   */
  setPharmacy: (pharmacy: ConsultPharmacySelection | null) => void;
  /** Patches the current reservation pharmacy (detail enrichment), keyed on the pair. */
  patchPharmacy: (
    pair: { marketplaceId: number; branchMarketplaceId: number },
    patch: Partial<ConsultPharmacySelection>,
  ) => void;
  addItem: (item: ItemInMarketplaceBasicResponse) => void;
  setItemQuantity: (itemId: number, quantity: number) => void;
  removeItem: (itemId: number) => void;
  updateRfzoDraft: (patch: Partial<ConsultRfzoDraft>) => void;
  /** Picks (or changes) the request pharmacy. */
  setRfzoPharmacy: (pharmacy: ConsultPharmacySelection | null) => void;
  patchRfzoPharmacy: (
    pair: { marketplaceId: number; branchMarketplaceId: number },
    patch: Partial<ConsultPharmacySelection>,
  ) => void;
  clearDraft: () => void;
}

const FE_PERSONAL_ID_TYPES: PersonalIdType[] = [
  "JMBG",
  "PERSONAL_CARD_NUMBER",
  "PASSPORT_NUMBER",
];

const DEFAULT_PERSONAL_ID_TYPE: PersonalIdType = "PERSONAL_CARD_NUMBER";

/** Estimated per-unit price: discounted branch price when present, else base. */
export const lineUnitPrice = (item: ItemInMarketplaceBasicResponse): number =>
  item.priceWithDiscount ?? item.price;

/** Estimated items subtotal from the DISCOUNTED branch price (201 is authoritative). */
export const estimatedItemsSubtotal = (lines: ConsultOrderLine[]): number =>
  lines.reduce((sum, line) => sum + lineUnitPrice(line.item) * line.quantity, 0);

const samePair = (
  a: ConsultPharmacySelection | null,
  b: { marketplaceId: number; branchMarketplaceId: number } | null,
): boolean =>
  a != null &&
  b != null &&
  a.marketplaceId === b.marketplaceId &&
  a.branchMarketplaceId === b.branchMarketplaceId;

const emptyDraft = (): ConsultOrderDraft => ({
  pharmacy: null,
  items: [],
  fulfillment: "PICKUP",
  addressMode: "SAVED",
  selectedAddressId: null,
  newAddress: null,
  saveNewAddress: false,
  // Loyalty is hidden (ITAM-2756/2757), so CASH is the only offered method
  // and also the default a fresh draft carries into the payload.
  paymentType: CONSULT_LOYALTY_PAYMENT_ENABLED ? "LOYALTY_POINTS" : "CASH",
  personalIdType: DEFAULT_PERSONAL_ID_TYPE,
  personalId: "",
  deliveryTimeSlot: null,
  noteForDelivery: "",
  deliveryConsent: false,
  deliveryZoneCheck: null,
  deliveryServiceFee: null,
  firstName: "",
  secondName: "",
  phoneNumber: "",
  note: "",
});

const emptyRfzoDraft = (): ConsultRfzoDraft => ({
  pharmacy: null,
  lbo: "",
  jmbg: "",
  firstName: "",
  secondName: "",
  userPhone: "",
  note: "",
  consent: false,
});

const rfzoDraftFromPrefill = (
  prefill: ConsultOrderPrefillResponse,
): ConsultRfzoDraft => {
  const user = prefill.user;
  return {
    ...emptyRfzoDraft(),
    lbo: user.lbo ?? "",
    firstName: user.firstName ?? "",
    secondName: user.secondName ?? "",
    userPhone: user.phoneNumber ?? "",
  };
};

const draftFromPrefill = (
  prefill: ConsultOrderPrefillResponse,
): ConsultOrderDraft => {
  const user = prefill.user;
  const prefillType =
    user.personalIdType && FE_PERSONAL_ID_TYPES.includes(user.personalIdType)
      ? user.personalIdType
      : DEFAULT_PERSONAL_ID_TYPE;
  return {
    ...emptyDraft(),
    personalIdType: prefillType,
    personalId: user.personalId ?? "",
    firstName: user.firstName ?? "",
    secondName: user.secondName ?? "",
    phoneNumber: user.phoneNumber ?? "",
  };
};

const ConsultOrderContext = createContext<ConsultOrderContextValue | undefined>(
  undefined,
);

export const ConsultOrderProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [prefill, setPrefill] = useState<ConsultOrderPrefillResponse | null>(
    null,
  );
  const [draft, setDraft] = useState<ConsultOrderDraft>(emptyDraft);
  const [rfzoDraft, setRfzoDraft] = useState<ConsultRfzoDraft>(emptyRfzoDraft);

  const hydrate = useCallback((next: ConsultOrderPrefillResponse) => {
    setPrefill(next);
    setDraft(draftFromPrefill(next));
    setRfzoDraft(rfzoDraftFromPrefill(next));
  }, []);

  const updateDraft = useCallback((patch: Partial<ConsultOrderDraft>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  }, []);

  // Items are branch-scoped (stock, price, item-in-marketplace ids), so a
  // different pharmacy invalidates every line. The delivery pre-checks are
  // too: the zone and the time slots are computed from the pharmacy's own
  // coordinates and the fee is the branch's SERVICE item. The caller's own
  // choices (fulfillment, address, document, note, consent) stay. Re-picking
  // the SAME pharmacy keeps everything.
  const setPharmacy = useCallback((pharmacy: ConsultPharmacySelection | null) => {
    setDraft((prev) => {
      if (samePair(prev.pharmacy, pharmacy)) {
        return { ...prev, pharmacy };
      }
      return {
        ...prev,
        pharmacy,
        items: [],
        deliveryZoneCheck: null,
        deliveryServiceFee: null,
        deliveryTimeSlot: null,
      };
    });
  }, []);

  const patchPharmacy = useCallback(
    (
      pair: { marketplaceId: number; branchMarketplaceId: number },
      patch: Partial<ConsultPharmacySelection>,
    ) => {
      setDraft((prev) => {
        if (!samePair(prev.pharmacy, pair) || prev.pharmacy == null) {
          return prev;
        }
        return { ...prev, pharmacy: { ...prev.pharmacy, ...patch } };
      });
    },
    [],
  );

  const addItem = useCallback((item: ItemInMarketplaceBasicResponse) => {
    setDraft((prev) => {
      const idx = prev.items.findIndex(
        (line) => line.item.id.itemId === item.id.itemId,
      );
      if (idx >= 0) {
        // Re-add merges: bump quantity, capped at the FRESH stock, and adopt
        // the fresh item snapshot (price/stock); the stock-overview modal
        // refetches on every open, so the incoming item is never staler than
        // the stored one. When stock dropped below the stored quantity this
        // clamps DOWN (the modal warns before the click).
        const existing = prev.items[idx];
        const nextQty = Math.min(existing.quantity + 1, item.quantity);
        const items = [...prev.items];
        items[idx] = { item, quantity: nextQty };
        return { ...prev, items };
      }
      return { ...prev, items: [...prev.items, { item, quantity: 1 }] };
    });
  }, []);

  const setItemQuantity = useCallback((itemId: number, quantity: number) => {
    setDraft((prev) => {
      const items = prev.items.map((line) => {
        if (line.item.id.itemId !== itemId) {
          return line;
        }
        // Clamp to [1, stock].
        const clamped = Math.max(1, Math.min(quantity, line.item.quantity));
        return { item: line.item, quantity: clamped };
      });
      return { ...prev, items };
    });
  }, []);

  const removeItem = useCallback((itemId: number) => {
    setDraft((prev) => ({
      ...prev,
      items: prev.items.filter((line) => line.item.id.itemId !== itemId),
    }));
  }, []);

  const updateRfzoDraft = useCallback((patch: Partial<ConsultRfzoDraft>) => {
    setRfzoDraft((prev) => ({ ...prev, ...patch }));
  }, []);

  // The request carries no branch-scoped lines, so a pharmacy change keeps
  // the rest of the draft as it is.
  const setRfzoPharmacy = useCallback(
    (pharmacy: ConsultPharmacySelection | null) => {
      setRfzoDraft((prev) => ({ ...prev, pharmacy }));
    },
    [],
  );

  const patchRfzoPharmacy = useCallback(
    (
      pair: { marketplaceId: number; branchMarketplaceId: number },
      patch: Partial<ConsultPharmacySelection>,
    ) => {
      setRfzoDraft((prev) => {
        if (!samePair(prev.pharmacy, pair) || prev.pharmacy == null) {
          return prev;
        }
        return { ...prev, pharmacy: { ...prev.pharmacy, ...patch } };
      });
    },
    [],
  );

  const clearDraft = useCallback(() => {
    setPrefill(null);
    setDraft(emptyDraft());
    setRfzoDraft(emptyRfzoDraft());
  }, []);

  const value = useMemo<ConsultOrderContextValue>(
    () => ({
      prefill,
      draft,
      rfzoDraft,
      hydrate,
      updateDraft,
      setPharmacy,
      patchPharmacy,
      addItem,
      setItemQuantity,
      removeItem,
      updateRfzoDraft,
      setRfzoPharmacy,
      patchRfzoPharmacy,
      clearDraft,
    }),
    [
      prefill,
      draft,
      rfzoDraft,
      hydrate,
      updateDraft,
      setPharmacy,
      patchPharmacy,
      addItem,
      setItemQuantity,
      removeItem,
      updateRfzoDraft,
      setRfzoPharmacy,
      patchRfzoPharmacy,
      clearDraft,
    ],
  );

  return (
    <ConsultOrderContext.Provider value={value}>
      {children}
    </ConsultOrderContext.Provider>
  );
};

export const useConsultOrderContext = (): ConsultOrderContextValue => {
  const ctx = useContext(ConsultOrderContext);
  if (!ctx) {
    throw new Error(
      "useConsultOrderContext must be used within a ConsultOrderProvider",
    );
  }
  return ctx;
};
